import { MidGrid } from "../../styles/emit-styles/Emit-styles";
import CardGrid from "./CardGrid";
import DistributionCard from "./DistributionCard";
import GraphCard from "./GraphCard";
import { compileFleetCompositionCardData, compileFleetDistanceBreakdownData, compileHomeViewGraphData, formatData, getEmissionsUnits, getFuelUnits } from "./UtilityFunctions";
import React, { useEffect, useState } from "react";
import { combinedEmissionsBars, emissionsByDrivetrainBars, fleetCondensedDrivetrains, fleetDrivetrains } from './HelperConstants';

const HomeView = ({ summaryData, graphData, defaultToMonthView, isTd, metricUnitPrefs, macroScale, userSettings, fleetHasAltFuels }) => {
    // TODO: Need to store viewDataByMonth, showDiscreteData, and splitByDrivetrain at the app level so that display preferences are maintained even when the graph component is destroyed (ie. user navigated to another page or updates app-level controls)
    const [viewDataByMonth, setViewDataByMonth] = useState(defaultToMonthView);
    const [formattedGraphData, setFormattedGraphData] = useState([]);
    const [hasReductionData, setHasReductionData] = useState(false);
    const [showDiscreteData, setShowDiscreteData] = useState(false);
    const [splitByDrivetrain, setSplitByDrivetrain] = useState(false);
    const [drivetrainsWithData, setDrivetrainsWithData] = useState({});

    useEffect(() => {
        setFormattedGraphData([]); // wipe graph data array
        setHasReductionData(false); // reset assumption of reduction data
        if (summaryData.ghgAvoided !== 0) setHasReductionData(true); // used to determine display of the reference line

        setDrivetrainsWithData(splitByDrivetrain ?
            {
                "gasolineGenerated": summaryData.gasolineGhgGenerated !== 0,
                "dieselGenerated": summaryData.dieselGhgGenerated !== 0,
                "cngGenerated": summaryData.dieselGhgGenerated !== 0,
                "bevGenerated": summaryData.bevGhgGenerated !== 0,
                "phevGenerated": summaryData.phevGhgGenerated !== 0,
                "bevAvoided": summaryData.bevGhgAvoided !== 0,
                "phevAvoided": summaryData.phevGhgAvoided !== 0,
                "cngAvoided": summaryData.cngGhgAvoided !== 0,
            } : {
                "generated": summaryData.ghgGenerated !== 0,
                "avoided": summaryData.ghgAvoided !== 0,
            }
        );

        const updatedGraphData = compileHomeViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData);
        setFormattedGraphData(updatedGraphData);
    }, [graphData, summaryData, viewDataByMonth, showDiscreteData, splitByDrivetrain]);

    const homeCardGridData = [
        {
            value: formatData('ghg', summaryData.ghgGenerated),
            label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
            labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
            caption: "Generated GHG Emissions",
            id: "generated-ghg-home-card"
        },
        {
            value: formatData('ghg', summaryData.ghgAvoided, null, fleetHasAltFuels),
            label: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, true),
            labelHover: getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false),
            caption: "Avoided GHG Emissions",
            id: "avoided-ghg-home-card"
        },
        {
            value: formatData('fuelCostSaved', summaryData.fuelCostSaved, userSettings, fleetHasAltFuels),
            caption: "Total Fuel Savings",
            id: "fuel-savings-home-card"
        },
        {
            value: formatData('fuelUnitsSaved', summaryData.fuelUnitsSaved, null, fleetHasAltFuels),
            label: getFuelUnits(metricUnitPrefs.volume, true),
            labelHover: getFuelUnits(metricUnitPrefs.volume, false),
            caption: "Total Fuel Reduction",
            id: "fuel-reduction-home-card"
        }
    ]

    const fleetCompositionCardData = compileFleetCompositionCardData(summaryData);
    const fleetDistanceBreakdownData = compileFleetDistanceBreakdownData(summaryData);

    const graphUnits = getEmissionsUnits('ghg', metricUnitPrefs.emissions, macroScale, false);

    return (
        <MidGrid columnLayout="minmax(460px, 1fr) 2fr">
            <div id="info-card-grid">
                <CardGrid
                    cardInfoArray={homeCardGridData}
                    cardHeight="130px"
                    cardMaxWidth="250px"
                    cardMinWidth="215px"
                    gridLayout="minmax(215px,250px) minmax(215px,250px)"
                />
                <DistributionCard
                    title="Fleet Composition"
                    subtitle="All Vehicles"
                    height="120px"
                    maxWidth="100%"
                    dataTypes={fleetDrivetrains}
                    data={fleetCompositionCardData}
                    dataType="allVehicles"
                    colorHoverValues
                    useLongLabels
                />
                <DistributionCard
                    title="Distance Breakdown"
                    subtitle="By Fuel Type"
                    height="120px"
                    maxWidth="700px"
                    dataTypes={fleetCondensedDrivetrains}
                    data={fleetDistanceBreakdownData}
                    dataType="simplifiedDistance"
                    metricUnitPrefs={metricUnitPrefs}
                    colorHoverValues
                    isTd={isTd}
                />
            </div>
            <GraphCard
                height="572px"
                width="100%"
                minWidth='550px'
                title={`${viewDataByMonth ? "Monthly" : "Weekly"} Emissions`}
                subtitle={`${splitByDrivetrain ? "By Fuel Type " : ""}${!showDiscreteData ? "(Cumulative)" : "(Discrete)"}`}
                data={formattedGraphData}
                barInfo={splitByDrivetrain ? emissionsByDrivetrainBars : combinedEmissionsBars}
                yAxisLabel={`GHG (${graphUnits})`}
                xAxisLabel={viewDataByMonth ? null : 'Week Starting'}
                units={graphUnits}
                drivetrainsWithData={drivetrainsWithData}
                hasReductionData={hasReductionData}
                viewDataByMonth={viewDataByMonth}
                showDiscreteData={showDiscreteData}
                splitByDrivetrain={splitByDrivetrain}
                setViewDataByMonth={setViewDataByMonth}
                setShowDiscreteData={setShowDiscreteData}
                setSplitByDrivetrain={setSplitByDrivetrain}
                combinedView
                metricUnitPrefs={metricUnitPrefs}
                userSettings={userSettings}
            />
        </MidGrid>
    )
};

export default HomeView;